/**
 * AgroTech — Anomaly Alerts  (Steps 3 + 4 of the 9-step intelligence loop)
 *
 * Step 3: AI-generated probable-cause narrative for each satellite-detected anomaly.
 * Step 4: Human verification gate — decision recorded to the immutable
 *         anomaly_verifications audit table via POST /api/anomalies/:id/verify.
 *
 * Verification history is loaded on demand and shown inside the review modal.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, XCircle, Clock, Zap, Navigation, History, ChevronDown, ChevronUp } from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import { anomalyApi, satelliteBoundariesApi, reportsApi } from '../../services/api';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import { Download } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────────────────────

type Severity = 'low' | 'medium' | 'high' | 'critical';
type AnomalyStatus = 'pending' | 'investigating' | 'resolved' | 'false_positive' | 'acknowledged';

interface Anomaly {
  id: string;
  boundary_name: string;
  boundary_id: string;
  anomaly_type: string;
  severity: Severity;
  status: AnomalyStatus;
  change_percentage: number | string;
  affected_area_hectares?: number;
  description?: string;
  detection_date: string;
  resolution_notes?: string;
  // Step 3 narrative fields
  ai_narrative?: string;
  probable_causes?: string[];
  baseline_acquisition_date?: string;
  current_acquisition_date?: string;
  baseline_ndvi?: number;
  current_ndvi?: number;
  confidence_score?: number;
  crop_type?: string;
}

interface Verification {
  id: string;
  action: string;
  actor_name: string;
  actor_email: string;
  notes?: string;
  rejection_reason?: string;
  created_at: string;
}

// ─── Step 4 decision options ─────────────────────────────────────────────────

const STEP4_ACTIONS = [
  {
    id: 'confirmed_drone'  as const,
    label: 'Confirm — Schedule Drone Inspection',
    icon: Navigation,
    colour: 'bg-blue-600 text-white hover:bg-blue-700',
    badge: 'triggers Step 5',
  },
  {
    id: 'confirmed_ground' as const,
    label: 'Confirm — Field Team Visit Only',
    icon: CheckCircle2,
    colour: 'bg-green-600 text-white hover:bg-green-700',
    badge: null,
  },
  {
    id: 'rejected'         as const,
    label: 'Reject — False Positive',
    icon: XCircle,
    colour: 'bg-gray-600 text-white hover:bg-gray-700',
    badge: null,
  },
  {
    id: 'escalated'        as const,
    label: 'Escalate to Management',
    icon: AlertTriangle,
    colour: 'bg-purple-600 text-white hover:bg-purple-700',
    badge: null,
  },
  {
    id: 'deferred'         as const,
    label: 'Defer — Remind me later',
    icon: Clock,
    colour: 'bg-amber-500 text-white hover:bg-amber-600',
    badge: null,
  },
];

const REJECTION_REASONS = [
  'Cloud shadow / data artifact',
  'Seasonal vegetation variation',
  'Planned irrigation or harvest',
  'Sensor calibration drift',
  'Weather event (known)',
  'Other',
];

// ─── Style maps ───────────────────────────────────────────────────────────────

const SEVERITY_STYLE: Record<Severity, string> = {
  low:      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  medium:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  high:     'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  critical: 'bg-red-200 text-red-900 font-bold dark:bg-red-900/60 dark:text-red-200',
};

const STATUS_STYLE: Record<AnomalyStatus, string> = {
  pending:        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  investigating:  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  resolved:       'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  false_positive: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
  acknowledged:   'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  pending:        'Pending Review',
  investigating:  'Action Taken',
  resolved:       'Resolved',
  false_positive: 'False Positive',
  acknowledged:   'Escalated',
};

const ACTION_LABELS: Record<string, string> = {
  confirmed_drone:  'Confirmed → Drone inspection',
  confirmed_ground: 'Confirmed → Ground visit',
  rejected:         'Rejected (false positive)',
  escalated:        'Escalated to management',
  deferred:         'Deferred',
};

// ─── Main Component ───────────────────────────────────────────────────────────

export const AnomalyAlertsPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [anomalies,       setAnomalies]       = useState<Anomaly[]>([]);
  const [stats,           setStats]           = useState<any>(null);
  const [fields,          setFields]          = useState<{ id: string; name: string }[]>([]);
  const [loading,         setLoading]         = useState(true);
  const [monitoring,      setMonitoring]      = useState(false);
  const [monitoringFieldId, setMonitoringFieldId] = useState('');
  const [error,           setError]           = useState<string | null>(null);
  const [success,         setSuccess]         = useState<string | null>(null);

  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');
  const [filterField,    setFilterField]    = useState('');

  // Step 4 modal state
  const [reviewingAnomaly,  setReviewingAnomaly]  = useState<Anomaly | null>(null);
  const [actionId,           setActionId]          = useState('');
  const [actionNote,         setActionNote]        = useState('');
  const [rejectionReason,    setRejectionReason]   = useState('');
  const [submitting,         setSubmitting]        = useState(false);
  // Editable AI analysis fields
  const [editType,        setEditType]        = useState('');
  const [editChange,      setEditChange]      = useState('');
  const [editArea,        setEditArea]        = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Verification history inside modal
  const [verifications,      setVerifications]     = useState<Verification[]>([]);
  const [loadingHistory,     setLoadingHistory]    = useState(false);
  const [showHistory,        setShowHistory]       = useState(false);

  // ── Load data ──────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyRes, statsRes] = await Promise.all([
        anomalyApi.getAnomalies({
          severity:   filterSeverity  || undefined,
          status:     filterStatus    || undefined,
          boundaryId: filterField     || undefined,
          vertical:   'agrotech',
        }),
        anomalyApi.getStats('agrotech'),
      ]);
      setAnomalies(anomalyRes.anomalies ?? []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, filterStatus, filterField]);

  useEffect(() => {
    load();
    satelliteBoundariesApi.getAll()
      .then((res) => {
        const all = res.data?.boundaries ?? [];
        setFields(all.filter((b: any) => b.metadata?.vertical === 'agrotech'));
      })
      .catch(() => {});
  }, [load]);

  // ── Load verification history ──────────────────────────────────────────────
  const loadVerifications = async (anomalyId: string) => {
    setLoadingHistory(true);
    try {
      const res = await anomalyApi.getVerifications(anomalyId);
      setVerifications(res.verifications ?? []);
    } catch {
      setVerifications([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // ── Open review modal ──────────────────────────────────────────────────────
  const openReview = (a: Anomaly) => {
    setReviewingAnomaly(a);
    setActionId('');
    setActionNote('');
    setRejectionReason('');
    setShowHistory(false);
    setVerifications([]);
    setEditType(a.anomaly_type ?? '');
    setEditChange(a.change_percentage != null ? String(a.change_percentage) : '');
    setEditArea(a.affected_area_hectares != null ? String(a.affected_area_hectares) : '');
    setEditDescription(a.description ?? '');
    loadVerifications(a.id);
  };

  // ── Run monitoring ─────────────────────────────────────────────────────────
  const handleRunMonitoring = async () => {
    setMonitoring(true);
    setError(null);
    try {
      if (monitoringFieldId) {
        await anomalyApi.detectChanges(monitoringFieldId);
        const selected = fields.find((f) => f.id === monitoringFieldId);
        setSuccess(`Monitoring complete for field: ${selected?.name ?? monitoringFieldId}`);
      } else {
        const res = await anomalyApi.runMonitoring('agrotech');
        setSuccess(`Monitoring complete — ${res.anomaliesDetected ?? 0} anomaly(s) detected`);
      }
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Monitoring failed');
    } finally {
      setMonitoring(false);
    }
  };

  // ── Step 4 decision ────────────────────────────────────────────────────────
  const handleAction = async () => {
    if (!reviewingAnomaly || !actionId) return;
    const action = STEP4_ACTIONS.find((a) => a.id === actionId)!;
    setSubmitting(true);
    try {
      // Build analyst correction suffix for audit trail
      const corrections: string[] = [];
      if (editType        !== reviewingAnomaly.anomaly_type)                         corrections.push(`type: ${editType}`);
      if (editChange      !== String(reviewingAnomaly.change_percentage ?? ''))      corrections.push(`NDVI Δ: ${editChange}%`);
      if (editArea        !== String(reviewingAnomaly.affected_area_hectares ?? '')) corrections.push(`area: ${editArea} ha`);
      if (editDescription !== (reviewingAnomaly.description ?? ''))                  corrections.push(`description: ${editDescription}`);
      const correctionNote = corrections.length ? ` [analyst corrections — ${corrections.join('; ')}]` : '';
      await anomalyApi.verify(reviewingAnomaly.id, {
        action: actionId as any,
        notes: `${actionNote.trim()}${correctionNote}`.trim() || undefined,
        rejection_reason: actionId === 'rejected' ? rejectionReason || undefined : undefined,
      });
      setSuccess(`Decision recorded: ${action.label}`);
      setReviewingAnomaly(null);
      setActionId('');
      setActionNote('');
      setRejectionReason('');
      setEditType(''); setEditChange(''); setEditArea(''); setEditDescription('');
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ?? e?.message ?? 'Failed to save decision');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount  = anomalies.filter((a) => a.status === 'pending').length;
  const criticalCount = anomalies.filter((a) => a.severity === 'critical' && a.status === 'pending').length;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-amber-500" /> Anomaly Alerts
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            AI-detected field anomalies (Step 3) — review and verify each alert (Step 4)
          </p>
          {(pendingCount > 0 || criticalCount > 0) && (
            <div className="flex items-center gap-2 mt-2">
              {criticalCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-red-200 text-red-900">
                  <Zap className="w-3 h-3" /> {criticalCount} critical pending
                </span>
              )}
              {pendingCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                  {pendingCount} awaiting review
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={monitoringFieldId}
            onChange={(e) => setMonitoringFieldId(e.target.value)}
            className={SELECT_CLS}
            disabled={monitoring}
          >
            <option value="">All Fields</option>
            {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Button onClick={handleRunMonitoring} loading={monitoring} variant="outline">
            <Zap className="w-4 h-4 mr-1" /> Run Monitoring
          </Button>
        </div>
      </div>

      {/* ── Alerts ── */}
      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Stats ── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total',          value: stats.total                    ?? 0, col: 'text-gray-900 dark:text-white' },
            { label: 'Critical',       value: stats.bySeverity?.critical     ?? 0, col: 'text-red-700 dark:text-red-400' },
            { label: 'High',           value: stats.bySeverity?.high         ?? 0, col: 'text-orange-700 dark:text-orange-400' },
            { label: 'Pending Review', value: stats.byStatus?.pending        ?? 0, col: 'text-amber-700 dark:text-amber-400' },
          ].map(({ label, value, col }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${col}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className={SELECT_CLS}>
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={SELECT_CLS}>
            <option value="">All Statuses</option>
            <option value="pending">Pending Review</option>
            <option value="investigating">Action Taken</option>
            <option value="acknowledged">Escalated</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
          </select>
          <select value={filterField} onChange={(e) => setFilterField(e.target.value)} className={SELECT_CLS}>
            <option value="">All Fields</option>
            {fields.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
          <Button size="sm" onClick={load}>Apply</Button>
          {(filterSeverity || filterStatus || filterField) && (
            <button
              onClick={() => { setFilterSeverity(''); setFilterStatus(''); setFilterField(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          )}
        </div>
      </Card>

      {/* ── Anomaly table ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {anomalies.length} anomaly{anomalies.length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-400">Click <strong>Review</strong> to read AI analysis and record Step 4 decision</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : anomalies.length === 0 ? (
          <div className="text-center py-14">
            <AlertTriangle className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No anomalies found</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>Run Monitoring</strong> to detect satellite changes</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <thead className="bg-gray-50 dark:bg-gray-700/50">
                <tr>
                  {['Field', 'Type', 'Severity', 'NDVI Change', `Area (${uom.area_unit_label})`, 'Detected', 'Status', 'Actions'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {anomalies.map((a) => (
                  <tr
                    key={a.id}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/30 ${a.severity === 'critical' && a.status === 'pending' ? 'bg-red-50/40 dark:bg-red-900/5' : ''}`}
                  >
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                      {a.boundary_name}
                      {a.crop_type && <span className="ml-1 text-xs text-gray-400">({a.crop_type})</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">{a.anomaly_type?.replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[a.severity]}`}>{a.severity}</span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono">
                      {a.change_percentage != null ? (
                        <span className={Number(a.change_percentage) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}>
                          {Number(a.change_percentage) > 0 ? '+' : ''}{Number(a.change_percentage).toFixed(1)}%
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                      {a.affected_area_hectares != null ? Number(a.affected_area_hectares).toFixed(1) : '—'}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {a.detection_date ? new Date(a.detection_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[a.status] ?? STATUS_STYLE.pending}`}>
                        {STATUS_LABELS[a.status] ?? a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant={a.status === 'pending' ? 'primary' : 'outline'}
                        onClick={() => openReview(a)}
                      >
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Step 4 Decision Modal ── */}
      {reviewingAnomaly && (
        <Modal onClose={() => setReviewingAnomaly(null)} panelClassName="max-w-2xl p-6">

            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Step 4 — Human Verification</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                  {reviewingAnomaly.boundary_name}
                  {reviewingAnomaly.crop_type && <span className="ml-1 text-xs">({reviewingAnomaly.crop_type})</span>}
                </p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[reviewingAnomaly.severity]}`}>
                {reviewingAnomaly.severity}
              </span>
            </div>

            {/* ── Step 3 AI Narrative — editable by analyst ── */}
            <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/10 p-4 mb-4">
              <p className="text-xs font-bold text-blue-700 dark:text-blue-400 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> Step 3 — AI Analysis
                <span className="text-[10px] font-normal text-blue-400 bg-blue-100 dark:bg-blue-900/40 px-1.5 py-0.5 rounded">editable</span>
              </p>

              {/* Confidence + imagery dates (read-only) */}
              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-blue-600 dark:text-blue-400 mb-3 font-mono">
                {reviewingAnomaly.baseline_ndvi != null && reviewingAnomaly.current_ndvi != null && (
                  <span>NDVI: {Number(reviewingAnomaly.baseline_ndvi).toFixed(3)} → {Number(reviewingAnomaly.current_ndvi).toFixed(3)}</span>
                )}
                {reviewingAnomaly.confidence_score != null && (
                  <span>Confidence: <strong>{reviewingAnomaly.confidence_score}%</strong></span>
                )}
                {(reviewingAnomaly.baseline_acquisition_date || reviewingAnomaly.current_acquisition_date) && (
                  <span>
                    Baseline: {reviewingAnomaly.baseline_acquisition_date ? new Date(reviewingAnomaly.baseline_acquisition_date).toLocaleDateString() : '—'}
                    {' · '}
                    Current: {reviewingAnomaly.current_acquisition_date ? new Date(reviewingAnomaly.current_acquisition_date).toLocaleDateString() : '—'}
                  </span>
                )}
              </div>

              {/* Editable fields */}
              <div className="grid grid-cols-2 gap-2 mb-2">
                <div>
                  <label className="block text-[11px] text-blue-600 dark:text-blue-400 mb-0.5">Anomaly Type</label>
                  <input
                    value={editType}
                    onChange={(e) => setEditType(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-blue-600 dark:text-blue-400 mb-0.5">NDVI Δ (%)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editChange}
                    onChange={(e) => setEditChange(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-blue-600 dark:text-blue-400 mb-0.5">Affected Area ({uom.area_unit_label})</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editArea}
                    onChange={(e) => setEditArea(e.target.value)}
                    className="w-full px-2 py-1.5 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[11px] text-blue-600 dark:text-blue-400 mb-0.5">Description / Probable Cause</label>
                <textarea
                  rows={2}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder="Analyst narrative or correction to AI-generated description…"
                  className="w-full px-2 py-1.5 text-xs border border-blue-200 dark:border-blue-700 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>

              {/* Full AI narrative (collapsed read-only ref) */}
              {reviewingAnomaly.ai_narrative && (
                <p className="mt-2 text-xs text-blue-500 dark:text-blue-400 italic leading-relaxed border-t border-blue-100 dark:border-blue-800 pt-2">
                  AI: {reviewingAnomaly.ai_narrative}
                </p>
              )}
            </div>

            {/* ── Verification history (collapsible) ── */}
            <button
              className="w-full flex items-center justify-between px-3 py-2 mb-4 rounded-lg bg-gray-100 dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              onClick={() => setShowHistory((v) => !v)}
            >
              <span className="flex items-center gap-2">
                <History className="w-4 h-4" />
                Decision history
                {!loadingHistory && verifications.length > 0 && (
                  <span className="px-1.5 py-0.5 rounded-full text-xs bg-gray-300 dark:bg-gray-600">{verifications.length}</span>
                )}
              </span>
              {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showHistory && (
              <div className="mb-4 space-y-2 max-h-48 overflow-y-auto">
                {loadingHistory ? (
                  <div className="flex justify-center py-4"><Spinner size="sm" /></div>
                ) : verifications.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-3">No decisions recorded yet.</p>
                ) : (
                  verifications.map((v) => (
                    <div key={v.id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-xs">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-semibold text-gray-800 dark:text-gray-200">
                          {ACTION_LABELS[v.action] ?? v.action}
                        </span>
                        <span className="text-gray-400 whitespace-nowrap">
                          {new Date(v.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-gray-500 dark:text-gray-400">
                        By: {v.actor_name ?? v.actor_email}
                        {v.rejection_reason && <> · Reason: {v.rejection_reason}</>}
                      </p>
                      {v.notes && <p className="mt-1 text-gray-600 dark:text-gray-300 italic">{v.notes}</p>}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* ── Action selection ── */}
            <div className="space-y-2 mb-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Your Decision</p>
              {STEP4_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setActionId(action.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-all ${
                      actionId === action.id
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                    {action.label}
                    {action.badge && (
                      <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">{action.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Rejection reason (shown only when rejected) */}
            {actionId === 'rejected' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rejection reason <span className="text-gray-400 font-normal">(used for AI feedback loop)</span>
                </label>
                <select
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  className={`w-full ${SELECT_CLS}`}
                >
                  <option value="">Select reason…</option>
                  {REJECTION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            )}

            {/* Optional note */}
            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Note <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <textarea
                rows={2}
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder="Agronomic context, reason for decision…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleAction} disabled={!actionId || submitting} loading={submitting} className="flex-1">
                Record Decision
              </Button>
              <Button
                variant="outline"
                type="button"
                onClick={() => reportsApi.downloadCertifiedPdf('anomaly_evidence', reviewingAnomaly.id)}
                className="flex items-center justify-center gap-1.5"
              >
                <Download className="w-4 h-4" /> Evidence PDF
              </Button>
              <Button variant="ghost" onClick={() => setReviewingAnomaly(null)} disabled={submitting}>
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default AnomalyAlertsPage;

// Made with Bob
